import { useState } from "react";
import { Stethoscope, Sparkles, Loader2, AlertOctagon, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ScopeToggle } from "./shared";
import { Button } from "@/components/ui/button";

const RISK = {
  baixo: "bg-emerald-100 text-emerald-700",
  moderado: "bg-amber-100 text-amber-700",
  alto: "bg-secondary/10 text-secondary",
};

export default function Diagnosis({ scope, setScope }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post(`/diagnosis/ai?scope=${scope}`);
      setResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8" data-testid="diagnosis-section">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-text-primary">Diagnóstico Financeiro</h2>
          <p className="text-text-secondary font-body mt-1">Análise por IA do seu cenário real e possíveis soluções.</p>
        </div>
        <ScopeToggle scope={scope} setScope={setScope} />
      </div>

      <div className="hero-navy rounded-3xl p-8 lg:p-10 text-white shadow-xl shadow-primary/20 relative overflow-hidden">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/5" />
        <div className="relative flex items-center justify-between flex-wrap gap-6">
          <div className="max-w-lg">
            <Stethoscope className="h-8 w-8 text-white/80" />
            <h3 className="font-heading text-2xl font-bold tracking-tight mt-3">Cruze transações e dívidas</h3>
            <p className="text-white/80 font-body mt-2 leading-relaxed">
              A IA analisa suas receitas, despesas e dívidas {scope === "business" ? "empresariais" : "pessoais"} cadastradas
              para identificar o principal gargalo e sugerir soluções práticas.
            </p>
          </div>
          <Button data-testid="generate-diagnosis" onClick={generate} disabled={loading}
            className="rounded-md bg-white text-primary hover:bg-white/90 h-11">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {loading ? "Analisando..." : "Gerar diagnóstico"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="space-y-6" data-testid="diagnosis-result">
          <div className="bg-surface border border-border rounded-2xl p-6 lg:p-8">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <h4 className="font-heading text-xl font-bold tracking-tight text-text-primary">Diagnóstico</h4>
              {result.risk_level && (
                <span data-testid="diagnosis-risk" className={`px-3 py-1 rounded-full text-xs font-body font-medium ${RISK[result.risk_level] || RISK.moderado}`}>
                  Risco {result.risk_level}
                </span>
              )}
            </div>
            <p className="text-text-secondary font-body leading-relaxed" data-testid="diagnosis-text">{result.diagnosis}</p>

            {result.bottleneck && (
              <div className="mt-5 flex items-start gap-3 bg-secondary/5 border border-secondary/20 rounded-xl p-4">
                <AlertOctagon className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-secondary font-body font-semibold">Gargalo principal</p>
                  <p className="text-text-primary font-body text-sm mt-1" data-testid="diagnosis-bottleneck">{result.bottleneck}</p>
                </div>
              </div>
            )}
          </div>

          {Array.isArray(result.solutions) && result.solutions.length > 0 && (
            <div>
              <h4 className="font-heading text-lg font-bold tracking-tight text-text-primary mb-4">Possíveis soluções</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="diagnosis-solutions">
                {result.solutions.map((s, i) => (
                  <div key={i} className="border border-primary/20 bg-primary/5 rounded-md p-6">
                    <div className="flex items-center gap-2 text-primary">
                      <Lightbulb className="h-4 w-4" />
                      <p className="uppercase text-xs tracking-[0.2em] font-body">Solução {i + 1}</p>
                    </div>
                    <h5 className="font-heading text-lg font-bold tracking-tight text-text-primary mt-3">{s.title}</h5>
                    <p className="text-text-secondary font-body mt-2 leading-relaxed text-sm">{s.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
